// Which character the blueprint loaders are currently hydrating for.
//
// The three graph refs (graphState / faceGraphState / eventGraphState) are
// global module singletons, so a character switch leaves fetches for the
// PREVIOUS character in flight and whichever resolves last wins — landing the
// old character's graph on the new one. For a splat character that graph has no
// plugin.ogs.renderer node, so the pipeline presents nothing and the avatar is
// absent until a reload, with no error anywhere.
//
// The owner is recorded HERE by the loader at fetch start, not read from the
// app's ambient "active character": that would make correctness depend on the
// id being updated before any fetch is issued, and a reorder would start
// dropping FRESH responses instead of stale ones — same symptom, harder cause.

let requested = null
let sequence = 0

/**
 * Record that a hydration has started for `characterId`; call BEFORE the await.
 *
 * `claim: false` is for a load that names a character only to pick storage keys
 * and must not re-arm it — the editor's first-run default, whose id is captured
 * at mount but which only fetches once a 404 comes back, by which time the user
 * may have switched. It still supersedes normally, in both directions.
 */
export function beginHydration(characterId, { claim = true } = {}) {
  const token = ++sequence
  if (claim && characterId != null) requested = characterId
  return {
    characterId,
    token,
    /** False once a newer hydration has claimed the graphs. */
    stillCurrent: () => isCurrentHydration(characterId, token),
  }
}

/**
 * Is a response still the one we want?
 *
 * Attributed loads are judged by character, NOT by token: two callers loading
 * the SAME character (App + BlueprintEditor both do, at boot) must both apply —
 * they write identical data, and dropping the loser leaves the editor's React
 * state unset, i.e. a blank canvas.
 *
 * An UNATTRIBUTED load (`?blueprint=` before a character resolved, a menu
 * preset) has no character to compare, so the token orders it: it wins until
 * any later load starts. It never touches `requested` — clearing the owner
 * would re-arm every stale response still in flight.
 */
export function isCurrentHydration(characterId, token = sequence) {
  if (characterId == null) return token === sequence
  if (requested == null) return true
  return characterId === requested
}

/** The character the most recent attributed hydration was started for. */
export function currentHydrationCharacter() {
  return requested
}

/** Tests only — forget the owner. */
export function resetHydrationOwner() {
  requested = null
  sequence = 0
}
